import { For, type JSX } from 'solid-js';

// The ARIA tablist — the ONE place a `role="tablist"` rail is spelled
// (`tabs-usage.test.ts`). Presenter: the caller owns which tab is selected and
// where the panels live. `<Tabs/>` pairs this with its panels; a caller whose
// panels are interleaved with its own gates (the donate wizard) renders the
// panels itself and addresses them by `tabPanelId`.
//
// Keyboard (WAI-ARIA APG tabs, automatic activation): the rail is ONE Tab stop —
// the selected tab carries `tabindex=0`, every other `-1` (roving tabindex).
// ArrowLeft/ArrowRight move focus with wrap-around, Home/End jump to the ends,
// and focusing a tab selects it unless it is `disabled`. A disabled tab stays
// focusable and announced (`aria-disabled`, never removed): a locked step the
// reader can find, with its reason beside it, beats one that appears later out
// of nowhere.

export type TabItem = {
  readonly id: string;
  /** Already translated. */
  readonly label: string;
  readonly disabled?: boolean;
  /** Rendered after the label inside the tab (a count, a state word). */
  readonly trail?: () => JSX.Element;
};

export type TabListProps = {
  /** The rail's accessible name, already translated. */
  readonly label: string;
  /** Namespaces the tab and panel ids — two rails on one page must not collide. */
  readonly idPrefix: string;
  readonly items: readonly TabItem[];
  readonly selected: string;
  readonly onSelect: (id: string) => void;
};

/** Element id of a tab; its panel names it with `aria-labelledby`. */
export const tabId = (prefix: string, id: string): string => `${prefix}-tab-${id}`;

/** Element id of a tab's panel; the tab points at it with `aria-controls`. */
export const tabPanelId = (prefix: string, id: string): string => `${prefix}-panel-${id}`;

/** The index focus lands on for a navigation key, or null when the key is not one. */
export function nextTabIndex(key: string, from: number, count: number): number | null {
  if (count === 0) return null;
  if (key === 'ArrowRight') return (from + 1) % count;
  if (key === 'ArrowLeft') return (from - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

export function TabList(props: TabListProps): JSX.Element {
  let list: HTMLDivElement | undefined;

  const pick = (item: TabItem): void => {
    if (!item.disabled) props.onSelect(item.id);
  };

  const onKeyDown = (event: KeyboardEvent, from: number): void => {
    const to = nextTabIndex(event.key, from, props.items.length);
    if (to === null) return;
    event.preventDefault();
    list?.querySelectorAll<HTMLElement>('[role="tab"]')[to]?.focus();
    const item = props.items[to];
    if (item !== undefined) pick(item);
  };

  return (
    <div class="tabs__list" role="tablist" aria-label={props.label} ref={list}>
      <For each={props.items}>
        {(item, index) => {
          const selected = (): boolean => props.selected === item.id;
          return (
            <button
              type="button"
              id={tabId(props.idPrefix, item.id)}
              role="tab"
              class="tabs__tab"
              tabindex={selected() ? 0 : -1}
              aria-selected={selected()}
              aria-disabled={item.disabled === true}
              aria-controls={tabPanelId(props.idPrefix, item.id)}
              onClick={() => pick(item)}
              onKeyDown={(event) => onKeyDown(event, index())}
            >
              {item.label}
              {item.trail?.()}
            </button>
          );
        }}
      </For>
    </div>
  );
}
